import { useContext } from "react";
import { ViewRequest, getEligibilityBasedQueryKey, getViewRequest, request } from "./request";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthenticationContext } from "../context";

export interface GetNotificationSettingsRequest {
  readonly identity: string;
  readonly external_member_id: string;
  readonly person_code: string;
  readonly plan_id: number;
}

export interface GetNotificationSettingsResponse {
  readonly identity: string;
  readonly external_member_id?: string;
  readonly person_code?: string;
  readonly plan_id?: number;
  readonly opt_in_to_push_notifications: boolean;
  readonly opt_in_to_emails: boolean;
}

export const getNotificationSettings = async (body: GetNotificationSettingsRequest) => {
  return request<GetNotificationSettingsRequest, GetNotificationSettingsResponse>({
    endpoint: "/user/notification_settings/get",
    body: { ...body },
    api: "authnz",
  });
};

export interface UpdateNotificationsRequest {
  readonly identity: string;
  readonly external_member_id: string;
  readonly person_code: string;
  readonly plan_id: number;
  readonly opt_in_to_emails: boolean;
  readonly opt_in_to_push_notifications: boolean;
}

export const updateNotificationSettings = async (body: UpdateNotificationsRequest) => {
  return request<UpdateNotificationsRequest, never>({
    endpoint: "/user/notification_settings/update",
    body: { ...body },
    api: "authnz",
  });
};

export interface MemberNotificationJSONAdherenceTips {
  readonly [index: number]: string;
  readonly alternative_drug_name?: string;
  readonly patient_pay_amount?: string;
}

export interface MemberNotificationJSONMessage {
  readonly claim_id?: number;
  readonly disclaimer_message?: string;
  readonly drug_name?: string;
  readonly main_message?: Array<string>;
  readonly main_message_subject?: string;
  readonly recent_claim_cost?: string;
  readonly most_recent_claim_id?: string;
  readonly supplemental_message?: MemberNotificationJSONAdherenceTips[];
  readonly supplemental_message_header?: string;
  readonly total_per_fill?: string;
  readonly dtp_name?: string;
  readonly submission_id?: number;
  readonly submission_type?: string;
  readonly submission_confirmation_number?: number;
  readonly event_type?: string;
  readonly documents?: Array<{ readonly id: number; readonly name: string }>;
  readonly expiry_date?: string;
  readonly hide_claim_link?: boolean;
}

export interface MemberNotification {
  readonly id?: string;
  readonly created_at?: string;
  readonly read_status?: "unread" | "read" | "hide";
  readonly push_notification_status?: "sent" | "unsent";
  readonly subject?: string;
  readonly message?: string;
  readonly json_message?: MemberNotificationJSONMessage;
  readonly claim_id?: number;
  readonly last_read_at?: string;
  readonly source?: string;
  readonly program?: string;
  readonly drug_name?: string;
  readonly external_member_id?: string;
  readonly client_id?: string;
  readonly person_code?: string;
}

type MemberNotificationKey = keyof MemberNotification;

export interface ListMemberNotificationsRequest extends Omit<MemberNotification, "recipient"> {
  readonly page_number?: number;
  readonly results_per_page?: number;
  readonly external_member_id?: string;
  readonly client_id?: string;
  readonly person_code?: string;
  readonly order_by?: [
    {
      [k in MemberNotificationKey]: "asc" | "desc";
    },
  ];
}

export interface ListMemberNotificationsResponse {
  readonly results: { readonly member_notification: MemberNotification }[];
  readonly result_count: string;
}

export const listMemberNotifications = async (authToken: string, body: ListMemberNotificationsRequest & Partial<ViewRequest>) => {
  return request<typeof body, ListMemberNotificationsResponse>({
    endpoint: "/member/notification_list",
    body: { ...body },
  }, authToken);
};

export const useListMemberNotificationsQuery = (filters?: ListMemberNotificationsRequest) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMemberNotifications(
          await getAuthToken(),
          {
            ...(filters || {}),
            ...getViewRequest(memberEligibility),
          });
      } else {
        return listMemberNotifications(
          await getAuthToken(),
          { ...(filters || {}) },
        );
      }
    },
    queryKey: [
      "memberNotifications", filters?.page_number || 1,
      ...getEligibilityBasedQueryKey(memberEligibility, filters.person_code),
    ],
  });

  return query;
};

export const getMemberNotification = async (
  authToken: string,
  body: {
    readonly id: string,
    readonly person_code?: string,
    readonly external_member_id?: string,
    readonly client_id?: string,
  } & Partial<ViewRequest>) => {

  return request<typeof body, MemberNotification>({
    endpoint: "/member/notification_get",
    body: { ...body },
  }, authToken);
};

export const useGetMemberNotificationQuery = (id: string, personCode?: string) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getMemberNotification(
          await getAuthToken(),
          {
            id: id,
            ...getViewRequest(memberEligibility),
          });
      } else {
        return getMemberNotification(await getAuthToken(),
        { id: id,
          external_member_id: memberEligibility.member.external_member_id,
          client_id: memberEligibility.plan.client_id,
          person_code: personCode,
        });
      }
    },
    queryKey: ["memberNotification", id, ...getEligibilityBasedQueryKey(memberEligibility, personCode)],
  });

  return query;
};

export interface UpdateNotificationRequest {
  readonly id: string;
  readonly read_status?: "unread" | "read" | "hide";
  readonly last_read_at?: string;
  readonly person_code?: string;
}

export const updateMemberNotification = async (authToken: string, body: UpdateNotificationRequest) => {
  return request<typeof body, MemberNotification>({
    endpoint: "/member/notification_update",
    body: { ...body },
  }, authToken);
};

export const useUpdateMemberNotificationMutation = () => {
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const queryClient = useQueryClient();
  const { getAuthToken } = useContext(AuthenticationContext);

  const mutation = useMutation({
    mutationFn: async (body: UpdateNotificationRequest) => {
      return updateMemberNotification(await getAuthToken(), body);
    },
    mutationKey: ["updateMemberNotification"],
    onSuccess: (data) => {
      queryClient.setQueryData(["memberNotification", data.id, ...getEligibilityBasedQueryKey(memberEligibility)], data);
    },
  });

  return mutation;
};
